import { forwardRef } from "react";
import { buildPrimitives } from "@/lib/geometry";
import { computeAssembly } from "@/lib/assembly";
import { profileCode } from "@/lib/catalogue";
import { jointPrims } from "@/lib/joints";
import { Primitives } from "@/components/Primitives";
import { SheetFrame, F } from "@/components/sheets/SheetFrame";
import { pickScale } from "@/components/SheetSVG";
import { Table } from "@/components/sheets/SheetTable";
import { subtypeOf } from "@/lib/iwin";
import { iwinAccessories, fmtQty } from "@/lib/hardware";

const CornerSketch = ({ x, y }) => (
  <g>
    <text x={x} y={y} fontSize={2.8} fontFamily={F} fontWeight="bold" fill="#000">CORNER ASSEMBLY (EXPLODED)</text>
    <polygon points={`${x + 4},${y + 8} ${x + 60},${y + 8} ${x + 50},${y + 18} ${x + 14},${y + 18}`} fill="#E6E8EB" stroke="#000" strokeWidth={0.3} transform={`translate(0 -2)`} />
    <polygon points={`${x + 4},${y + 14} ${x + 14},${y + 24} ${x + 14},${y + 68} ${x + 4},${y + 68}`} fill="#E6E8EB" stroke="#000" strokeWidth={0.3} transform={`translate(-1 2)`} />
    <rect x={x + 22} y={y + 30} width={18} height={4} fill="#9CA3AF" stroke="#000" strokeWidth={0.25} />
    <rect x={x + 22} y={y + 30} width={4} height={18} fill="#9CA3AF" stroke="#000" strokeWidth={0.25} />
    <line x1={x + 9} y1={y + 16} x2={x + 22} y2={y + 31} stroke="#B45309" strokeWidth={0.2} strokeDasharray="1 0.8" />
    <line x1={x + 40} y1={y + 30} x2={x + 46} y2={y + 16} stroke="#B45309" strokeWidth={0.2} strokeDasharray="1 0.8" />
    {[["45° mitre cut – head / sill / jamb", 12], ["Aluminium corner cleat, crimped or pinned", 32], ["Corner sealant applied to mitre faces", 46], ["Glazing bead cut 90° & snapped in after glazing", 54], ["Gaskets continuous, vulcanised / glued at corners", 62]].map(([t, dy]) => (
      <text key={t} x={x + 46} y={y + dy + 8} fontSize={2.2} fontFamily={F} fill="#000">{t}</text>
    ))}
  </g>
);

export const AssemblySheet = forwardRef(({ item, tb, sheetNo, total }, ref) => {
  const { prims, bounds } = buildPrimitives(item, { dims: true, labels: false });
  const asm = computeAssembly(item);
  const aw = 125, ah = 150;
  const scale = pickScale(bounds, aw, ah);
  const k = 1 / scale;
  const ox = 15 + (aw - bounds.w * k) / 2 - bounds.x * k, oy = 30 + (ah - bounds.h * k) / 2 - bounds.y * k;
  const E = Math.max(item.width, item.height), tr = E * 0.022;
  const tagPrims = asm.tags.flatMap((t) => [
    { t: "circle", cx: t.x, cy: t.y, r: tr, fill: t.glass ? "#E0F2FE" : "#FFFFFF", weight: "medium", layer: "TAG" },
    { t: "text", x: t.x, y: t.y + tr * 0.4, text: t.mark, h: tr * 1.05, anchor: "middle", layer: "TAG", bold: true },
  ]);
  const q = item.quantity || 1;
  const s = subtypeOf(item), hw = s ? iwinAccessories(item) : null, hs = E * 0.017;
  const hwPrims = (hw || []).flatMap((h) => h.pos.flatMap(([x, y]) => [
    { t: "rect", x: x - hs, y: y - hs * 0.7, w: hs * 2, h: hs * 1.4, fill: "#FEF3C7", weight: "thin", layer: "HARDWARE", dxf: "HARDWARE_TAG" },
    { t: "text", x, y: y + hs * 0.32, text: h.mark, h: hs * 0.85, anchor: "middle", layer: "HARDWARE", dxf: "HARDWARE_TAG", bold: true },
  ]));
  const mRows = asm.members.map((m) => [m.mark, m.name, profileCode(item, m.name) || "—", m.len, m.qty, m.qty * q, m.cut]);
  const gRows = asm.glass.map((g) => [g.mark, `${g.w} × ${g.h}`, g.spec, g.qty, g.qty * q]);
  const aRows = hw ? hw.map((h) => [h.mark, h.name, h.spec, fmtQty(h), fmtQty(h, q), h.basis]) : asm.accessories;
  const rh = Math.min(4.6, (244 - 24) / (mRows.length + gRows.length + aRows.length + 3));
  const tableY = 34, gY = tableY + (mRows.length + 1) * rh + 8, aY = gY + (gRows.length + 1) * rh + 8;
  return (
    <SheetFrame svgRef={ref} item={item} tb={tb} sheetNo={sheetNo} total={total} suffix="A" kind="ASSEMBLY DRAWING & CUT LIST"
      title="ASSEMBLY DRAWING" subtitle={`MEMBER MARKS · CUT LENGTHS (APPROX.) · GLASS SIZES · ${hw ? "IWIN-S ACCESSORIES" : "ACCESSORIES"} · ${item.profile_system || "YKK AP"}`} scaleText={`1:${scale} @ A3`} scaleValue={scale}>
      <g transform={`translate(${ox} ${oy}) scale(${k})`}>
        <Primitives prims={[...prims, ...tagPrims, ...hwPrims, ...jointPrims(item)]} u={0.18 / k / 1.3} palette="print" font={F} />
      </g>
      <CornerSketch x={16} y={192} />
      {hw && <text x={16} y={187} fontSize={2.2} fontFamily={F} fill="#92400E" data-testid="hardware-marker-legend">{`H1–H${hw.length} = IWIN-S accessory positions (yellow tags) · see accessories table`}</text>}
      <Table x={146} y={tableY} rh={rh} testid="cut-list-table" title="PROFILE CUT LIST" rows={mRows}
        cols={[{ h: "MARK", w: 10 }, { h: "MEMBER", w: 34 }, { h: "PROFILE CODE", w: 20 }, { h: "LENGTH mm", w: 18 }, { h: "QTY/UNIT", w: 15 }, { h: `TOTAL ×${q}`, w: 16 }, { h: "CUT", w: 36 }]} />
      <Table x={146} y={gY} rh={rh} testid="glass-schedule-table" title="GLASS SCHEDULE (SIZE INCL. 14mm BITE)" rows={gRows}
        cols={[{ h: "MARK", w: 11 }, { h: "SIZE W × H", w: 26 }, { h: "SPECIFICATION", w: 70 }, { h: "QTY/UNIT", w: 18 }, { h: `TOTAL ×${q}`, w: 24 }]} />
      {hw ? (
        <Table x={146} y={aY} rh={rh} testid="iwin-accessories-table" title={`IWIN-S ACCESSORIES & HARDWARE · ${s.name.toUpperCase()}`} rows={aRows}
          cols={[{ h: "MARK", w: 9 }, { h: "ACCESSORY", w: 33 }, { h: "SPEC / MATERIAL", w: 43 }, { h: "QTY/UNIT", w: 14 }, { h: `TOTAL ×${q}`, w: 14 }, { h: "BASIS", w: 36 }]} />
      ) : (
        <Table x={146} y={aY} rh={rh} testid="accessories-table" title="ACCESSORIES & SEALS (PER UNIT, APPROX.)" rows={aRows}
          cols={[{ h: "ITEM", w: 100 }, { h: "QTY", w: 49 }]} />
      )}
      <text x={16} y={284.5} fontSize={2} fontFamily={F} fill="#555">
        {hw ? "Roller type by leaf weight (single up to 80 kg, double up to 150 kg, triple up to 250 kg). Cut lengths approximate — confirm deductions with the Bhoruka / YKK AP IWIN-S fabrication manual." : "Cut lengths are approximate (sightline based). Confirm deductions, cleats and hardware with the YKK AP fabrication manual before cutting."}
      </text>
    </SheetFrame>
  );
});
AssemblySheet.displayName = "AssemblySheet";
